import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import {assessmentApi} from '../../api/openapi-axios-client';
import {callApi} from '../../api/helpers';
import {CareerCluster} from '../../types';
import {getClusterImage} from '../../helpers/images';
import { trackBehaviorEvent } from '../../services/behaviorTracking/trackBehaviorEvent';

const LibraryClustersPage: React.FC = () => {
  const [careerClusters, setCareerClusters] = useState<CareerCluster[]>([]);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  useEffect(() => {
    const getClusters = async () => {
      const response = await callApi<CareerCluster[]>(() => assessmentApi.careerData.getONetCareerClusters());

      if(response && response.length) {
        setCareerClusters(response);
      }
    }
    
    if(!careerClusters.length) {
      getClusters();
    }
  }, [careerClusters.length]);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      <Typography sx={{
        fontWeight: 900,
        fontSize: { xs: 20, sm: 24, md: 28 },
        mb: { xs: 1, sm: 2 },
        color: 'text.primary',
        textAlign: { xs: 'center', sm: 'left' }
      }}>
        Career Industries
      </Typography>
      <Typography sx={{
        fontSize: { xs: 14, sm: 16, md: 18 },
        mb: { xs: 2, sm: 3, md: 4 },
        color: 'text.primary',
        textAlign: { xs: 'center', sm: 'left' }
      }}>
        Explore over 50,000+ career profiles to find out what they pay, expected growth, recommended education, and more.
      </Typography>
      <Grid
        container
        spacing={{ xs: 2, sm: 3, md: 4 }}
        aria-label="Library clusters list"
      >
        {careerClusters.length > 0 && careerClusters
          .sort((a, b) => Number(a.code) - Number(b.code))
          .map(cluster => (
            <Grid
              key={cluster.id}
              size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
            >
              <Card
                sx={{
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => {
                    trackBehaviorEvent('video-selected-from-explore', { clusterId: cluster.id });
                    navigate(`/library/cluster/${cluster.id}`);
                  }}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                >
                  <CardMedia
                    component="img"
                    height={isMobile ? 160 : 175}
                    image={getClusterImage(cluster.code)}
                    alt={`Cluster illustration for ${cluster.title || cluster.code || 'career cluster'}`}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        fontSize: { xs: 14, sm: 15, md: 16 },
                        color: 'text.primary',
                        lineHeight: 1.3
                      }}
                    >
                      {cluster.title}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
      </Grid>
    </Container>
  );
};

export default LibraryClustersPage; 